import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";

interface LocationState {
    candidateId: string;
}

export default function CandidateEnroll() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const [cameraAvailable, setCameraAvailable] = useState(false);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);

    const location = useLocation();
    const navigate = useNavigate();
    const { candidateId } = (location.state || {}) as LocationState;

    // 🎥 Initialize camera or fall back to upload mode
    async function initializeCamera() {
        setError(null);
        setLoading(true);

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasCamera = devices.some((d) => d.kind === "videoinput");

            if (!hasCamera) {
                console.warn("🚫 No camera detected.");
                setError("No camera detected. Please upload a selfie instead.");
                setCameraAvailable(false);
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) videoRef.current.srcObject = stream;
            setCameraAvailable(true);
        } catch (err) {
            console.error("Camera error:", err);
            setError("Camera access denied or unavailable. Please upload a selfie instead.");
            setCameraAvailable(false);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!candidateId) {
            navigate("/candidate/login");
            return;
        }
        initializeCamera();

        return () => {
            // stop all video tracks when leaving
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [candidateId, navigate]);

    // 📸 Take a snapshot
    function takeSnapshot() {
        if (!videoRef.current || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        setSnapshotUrl(canvas.toDataURL("image/jpeg"));
    }

    // ☁️ Upload image → Supabase Storage + Face API
    async function handleSubmit() {
        if (!snapshotUrl) return;
        setUploading(true);
        setError(null);

        try {
            console.log("🧩 Updating candidate:", candidateId);
            const blob = await (await fetch(snapshotUrl)).blob();
            const fileName = `candidate_${candidateId}_${Date.now()}.jpg`;

            // 1️⃣ Upload to Supabase Storage
            const { error: uploadError } = await supabase.storage
                .from("mugshots")
                .upload(fileName, blob, { contentType: "image/jpeg", upsert: true });
            if (uploadError) throw uploadError;

            const publicUrl = supabase.storage
                .from("mugshots")
                .getPublicUrl(fileName).data.publicUrl;
            console.log("🖼️ Uploaded selfie URL:", publicUrl);

            // 2️⃣ Send to Face API
            const formData = new FormData();
            formData.append("Image", blob, fileName);

            const response = await fetch(
                "https://secureapi-prod-dccvbqhwh6fkeyfh.centralus-01.azurewebsites.net/api/face/embed",
                { method: "POST", body: formData }
            );
            if (!response.ok) throw new Error(`Face API error: ${response.status}`);
            const { embedding } = await response.json();
            console.log("🧠 Received embedding length:", embedding.length);

            // 3️⃣ Update candidate record
            const { error: updateError } = await supabase
                .from("candidates")
                .update({
                    mugshot_url: publicUrl,
                    embedding_vector: embedding as number[],
                })
                .eq("id", candidateId);
            if (updateError) throw updateError;

            // 4️⃣ Get test assignment
            const { data: assignment } = await supabase
                .from("candidateassignments")
                .select("test_id")
                .eq("candidate_id", candidateId)
                .limit(1)
                .single();

            setSuccess(true);
            setTimeout(() => {
                if (assignment?.test_id)
                    navigate(`/candidate/test/${assignment.test_id}`);
                else navigate("/candidate/notest");
            }, 1500);
        } catch (err: any) {
            console.error("❌ Enrollment error:", err);
            setError(err.message);
        } finally {
            setUploading(false);
        }
    }

    // 🧱 Manual upload fallback (no camera)
    if (!cameraAvailable && error?.includes("upload")) {
        return (
            <div className="max-w-md mx-auto p-6 text-center">
                <h1 className="text-2xl font-bold mb-4">Candidate Enrollment</h1>
                <p className="text-gray-600 mb-3">{error}</p>

                <input
                    type="file"
                    accept="image/*"
                    className="border p-2 w-full mb-3"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                            const blobUrl = URL.createObjectURL(file);
                            setSnapshotUrl(blobUrl);
                        }
                    }}
                />

                {snapshotUrl && (
                    <>
                        <img
                            src={snapshotUrl}
                            alt="Uploaded selfie preview"
                            className="w-full rounded mb-3"
                        />
                        <button
                            onClick={handleSubmit}
                            className="w-full bg-green-600 text-white py-2 rounded"
                            disabled={uploading}
                        >
                            {uploading ? "Processing..." : "Submit Enrollment"}
                        </button>
                    </>
                )}
            </div>
        );
    }

    if (loading) return <p className="p-6 text-center">🎥 Initializing camera...</p>;

    // ✅ Camera flow
    return (
        <div className="max-w-md mx-auto p-6 text-center">
            <h1 className="text-2xl font-bold mb-4">Candidate Enrollment</h1>

            {error && <p className="text-red-500 mb-4">{error}</p>}
            {success && <p className="text-green-600 mb-4">✅ Enrollment completed!</p>}

            <div className="relative border rounded mb-4 overflow-hidden shadow">
                {!snapshotUrl ? (
                    <video ref={videoRef} autoPlay playsInline className="w-full bg-black rounded" />
                ) : (
                    <img src={snapshotUrl} alt="Selfie preview" className="w-full rounded" />
                )}
                <canvas ref={canvasRef} width={320} height={240} hidden />
            </div>

            {!snapshotUrl ? (
                <button
                    onClick={takeSnapshot}
                    className="w-full bg-indigo-600 text-white py-2 rounded"
                >
                    📸 Take Selfie
                </button>
            ) : (
                <>
                    <button
                        onClick={() => setSnapshotUrl(null)}
                        className="w-full bg-gray-400 text-white py-2 rounded mb-2"
                        disabled={uploading}
                    >
                        Retake
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="w-full bg-green-600 text-white py-2 rounded"
                        disabled={uploading}
                    >
                        {uploading ? "Processing..." : "Submit Enrollment"}
                    </button>
                </>
            )}
        </div>
    );
}
